import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, BookOpen, FileText, ChevronRight, Award, Star, X, ClipboardList } from 'lucide-react';
import api from '../api/axios';

const RatingModal = ({ courseTitle, courseId, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    if (rating === 0) return;
    setSaving(true);
    api.post('/courses/' + courseId + '/rate', { rating, comment })
      .then(() => { setSaved(true); setSaving(false); setTimeout(() => onClose(), 1500); })
      .catch(() => setSaving(false));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 24, padding: '40px 36px', width: '100%', maxWidth: 420, textAlign: 'center', boxShadow: '0 32px 80px rgba(0,0,0,0.3)', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={16} /></button>
        {saved ? (
          <div><div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div><div style={{ fontWeight: 700, fontSize: 20, color: '#1a1a2e' }}>Yorumunuz Kaydedildi!</div></div>
        ) : (
          <>
            <div style={{ width: 90, height: 90, margin: '0 auto 20px', background: 'linear-gradient(135deg, #ffd700, #ffa500)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(255,165,0,0.35)' }}>
              <Star size={40} color="white" fill="white" />
            </div>
            <div style={{ fontWeight: 800, fontSize: 18, color: '#1a1a2e', marginBottom: 4 }}>Tebrikler!</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#E30613', marginBottom: 4 }}>{courseTitle}</div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 20 }}>Eğitimini Tamamladınız!</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Bu eğitim hakkında ne düşünüyorsunuz?</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
              {[1,2,3,4,5].map(star => (
                <div key={star} onClick={() => setRating(star)} onMouseEnter={() => setHover(star)} onMouseLeave={() => setHover(0)} style={{ cursor: 'pointer' }}>
                  <Star size={36} color={(hover||rating)>=star?'#ffd700':'#d1d5db'} fill={(hover||rating)>=star?'#ffd700':'transparent'} strokeWidth={1.5} />
                </div>
              ))}
            </div>
            <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Yorumunuz (isteğe bağlı)"
              style={{ width: '100%', minHeight: 90, padding: '12px 14px', border: '1.5px solid #e5e7eb', borderRadius: 12, fontSize: 14, outline: 'none', resize: 'none', fontFamily: 'Inter, sans-serif', boxSizing: 'border-box', color: '#374151', marginBottom: 16 }} />
            <button onClick={handleSave} disabled={saving||rating===0}
              style={{ width: '100%', padding: '13px', background: rating===0?'#d1d5db':'linear-gradient(135deg, #34c759, #00b4d8)', color: 'white', border: 'none', borderRadius: 12, cursor: rating===0?'not-allowed':'pointer', fontWeight: 700, fontSize: 15 }}>
              {saving?'Kaydediliyor...':'Puan ve Yorumu Kaydet'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default function CoursePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const progressTimer = useRef(null);
  const maxTimeWatched = useRef(0);
  
  const [course, setCourse] = useState(null);
  const [allVideos, setAllVideos] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [progressMap, setProgressMap] = useState({});
  const [courseCertCode, setCourseCertCode] = useState(null);
  
  // Quiz Stateleri
  const [quiz, setQuiz] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizState, setQuizState] = useState('intro'); // intro, taking, result
  const [answers, setAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);

  const [activeTab, setActiveTab] = useState('info');
  const [note, setNote] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);
  const [savedNotes, setSavedNotes] = useState([]);
  const [showNextBanner, setShowNextBanner] = useState(false);
  const [nextVideo, setNextVideo] = useState(null);
  const [seekWarning, setSeekWarning] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (activeVideo && !showQuiz) {
      setNote('');
      api.get('/notes/' + activeVideo.id)
        .then(r => { setNote(''); setSavedNotes(r.data || []); })
        .catch(() => { setNote(''); setSavedNotes([]); });
    }
  }, [activeVideo?.id, showQuiz]);

  useEffect(() => {
    api.get('/courses/' + id).then(async r => {
      setCourse(r.data);
      const videos = [];
      r.data.sections?.forEach(section => { section.videos?.forEach(video => { videos.push({ ...video, sectionTitle: section.title }); }); });
      setAllVideos(videos);
      
      const progressRes = await api.get('/videos/progress/' + r.data.id);
      const pMap = {};
      progressRes.data.forEach(p => { if (p.is_completed) pMap[p.video_id] = true; });
      setProgressMap(pMap);

      // Sınavı getir
      let fetchedQuiz = null;
      try {
        const quizRes = await api.get('/quizzes/course/' + id);
        if (quizRes.data) {
          setQuiz(quizRes.data);
          fetchedQuiz = quizRes.data;
          if (quizRes.data.lastResult) setQuizResult(quizRes.data.lastResult);
        }
      } catch(e) { console.log('Quiz yüklenmedi'); }
      
      const vidsDone = videos.length > 0 && videos.every(v => pMap[v.id]);
      const quizDone = !fetchedQuiz || fetchedQuiz?.lastResult?.passed;

      if (vidsDone && quizDone) {
        setCompleted(true);
        api.get('/certificates/my').then(certRes => {
          const cert = certRes.data.find(c => c.course_id === r.data.id);
          if (cert) setCourseCertCode(cert.certificate_code);
        });
      }

      if (videos.length > 0) {
        const lastUnfinished = videos.find(v => !pMap[v.id]);
        const startVideo = lastUnfinished || videos[videos.length - 1];
        const startIndex = videos.findIndex(v => v.id === startVideo.id);
        setActiveVideo(startVideo); setActiveIndex(startIndex);
      }
    });
  }, [id]);

  useEffect(() => {
    const handleVisibility = () => { if (document.hidden && videoRef.current && !showQuiz) videoRef.current.pause(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [showQuiz]);

  useEffect(() => { 
    if (!showQuiz) {
        maxTimeWatched.current = 0; setShowNextBanner(false); setSeekWarning(false); 
    }
  }, [activeVideo, showQuiz]);

  const totalVideos = allVideos.length;
  const completedVideos = Object.values(progressMap).filter(Boolean).length;
  let percent = totalVideos > 0 ? Math.round(completedVideos * 100 / totalVideos) : 0;
  if (quiz && (!quizResult || !quizResult.passed) && percent === 100) percent = 99;
  const allVideosDone = totalVideos > 0 && completedVideos === totalVideos;
  const totalDuration = course?.sections?.reduce((a, s) => a + (s.videos?.reduce((b, v) => b + (v.duration || 0), 0) || 0), 0) || 0;

  const saveProgress = useCallback((isCompleted) => {
    const video = videoRef.current;
    if (!activeVideo || showQuiz) return;
    
    api.post('/videos/progress', { video_id: activeVideo.id, watched_seconds: Math.floor(video?.currentTime || 0), is_completed: isCompleted })
      .then(res => { 
        if (isCompleted) {
            setProgressMap(p => {
                const newMap = { ...p, [activeVideo.id]: true };
                const vidsDone = allVideos.length > 0 && allVideos.every(v => newMap[v.id]);
                const quizDone = !quiz || quizResult?.passed;
                if (vidsDone && quizDone && !completed) {
                    setCompleted(true);
                    setTimeout(() => setShowRatingModal(true), 1500);
                }
                return newMap;
            });
        }
      })
      .catch(err => console.error('Progress kaydedilemedi:', err));
  }, [activeVideo, allVideos, quiz, quizResult, showQuiz, completed]);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || showQuiz) return;
    const isAlreadyCompleted = activeVideo && progressMap[activeVideo.id];
    if (!isAlreadyCompleted && video.currentTime > maxTimeWatched.current + 1) {
      video.currentTime = maxTimeWatched.current;
      setSeekWarning(true);
      setTimeout(() => setSeekWarning(false), 2000);
    } else { maxTimeWatched.current = Math.max(video.currentTime, maxTimeWatched.current); }
    
    clearTimeout(progressTimer.current);
    progressTimer.current = setTimeout(() => {
      const isCompleted = video.duration > 0 && (video.currentTime / video.duration) >= 0.95;
      saveProgress(isCompleted);
    }, 3000);
  };

  const goToVideo = (video, index) => { 
    if (video.isQuiz) {
      setShowQuiz(true);
      setQuizState(quizResult ? 'result' : 'intro');
    } else {
      setShowQuiz(false);
      setActiveVideo(video); 
      setActiveIndex(index); 
    }
    setShowNextBanner(false); 
  };

  const handleEnded = () => {
    clearTimeout(progressTimer.current);
    saveProgress(true);
    const nextIndex = activeIndex + 1;
    if (nextIndex < allVideos.length) {
      setNextVideo(allVideos[nextIndex]); setShowNextBanner(true);
      setTimeout(() => goToVideo(allVideos[nextIndex], nextIndex), 5000);
    } else if (quiz && (!quizResult || !quizResult.passed)) {
      setNextVideo({ title: 'Eğitim Sonu Sınavı', isQuiz: true });
      setShowNextBanner(true);
      setTimeout(() => goToVideo({ isQuiz: true }, nextIndex), 5000);
    }
  };

  const handleQuizSubmit = () => {
    const answersArray = quiz.questions.map((q, i) => answers[i] !== undefined ? answers[i] : -1);
    api.post('/quizzes/' + quiz.id + '/submit', { answers: answersArray })
      .then(r => {
        setQuizResult(r.data);
        setQuizState('result');
        if (r.data.passed) {
          setCompleted(true);
          setTimeout(() => setShowRatingModal(true), 1500);
          api.get('/certificates/my').then(certRes => {
            const cert = certRes.data.find(c => c.course_id === id);
            if (cert) setCourseCertCode(cert.certificate_code);
          });
        }
      }).catch(e => alert('Sınav gönderilirken hata oluştu.'));
  };

  const handleViewCertificate = () => {
    if (courseCertCode) window.open('/api/certificates/download/' + courseCertCode + '?token=' + token, '_blank');
    else navigate('/sertifikalar');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: isMobile ? '10px 16px' : '10px 24px', minHeight: 56, display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => navigate('/panel')} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
          <ArrowLeft size={15} /> {!isMobile && 'Geri'}
        </button>
        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
        <span style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, color: 'white', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {course?.title}
        </span>
        {completed && (
          <div style={{ flexShrink: 0, background: 'rgba(52,199,89,0.2)', border: '1px solid rgba(52,199,89,0.3)', color: '#34c759', borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={13} /> {!isMobile && 'Tamamlandı'}
          </div>
        )}
      </div>

      {seekWarning && !showQuiz && <div style={{ background: 'rgba(239,68,68,0.95)', color: 'white', padding: '10px 24px', textAlign: 'center', fontWeight: 700, fontSize: 14 }}>Bu videoyu ileri saramazsınız!</div>}

      {completed && (
        <div style={{ background: 'linear-gradient(135deg, rgba(52,199,89,0.25), rgba(227,6,19,0.2))', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(52,199,89,0.2)', color: 'white', padding: '16px 20px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, lineHeight: 1.4 }}>
            🎉 Tebrikler! Eğitimi Tamamladınız! Sertifikanızı görüntülemek için tıklayın.
          </span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button onClick={() => setShowRatingModal(true)} style={{ background: 'rgba(255,165,0,0.2)', border: '1px solid rgba(255,165,0,0.4)', color: '#ffd700', borderRadius: 10, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}><Star size={14} fill="#ffd700" color="#ffd700" /> Yorum Yap</button>
            <button onClick={handleViewCertificate} style={{ background: 'linear-gradient(135deg, #34c759, #00b4d8)', border: 'none', color: 'white', borderRadius: 10, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}><Award size={14} /> Sertifikayı Gör</button>
          </div>
        </div>
      )}

      {showNextBanner && nextVideo && (
        <div style={{ background: 'rgba(227,6,19,0.2)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(227,6,19,0.3)', color: 'white', padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, fontSize: 14 }}>
          <ChevronRight size={16} /> {nextVideo.isQuiz ? 'Sınav başlıyor:' : 'Sonraki video 5 saniye icinde başlıyor:'} <strong>{nextVideo.title}</strong>
          <button onClick={() => goToVideo(nextVideo, activeIndex+1)} style={{ background: '#E30613', border: 'none', color: 'white', borderRadius: 8, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Şimdi Geç</button>
          <button onClick={() => setShowNextBanner(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>İptal</button>
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, maxWidth: 1400, margin: '0 auto', width: '100%', padding: '20px', gap: 20, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
        <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
          <div style={{ background: showQuiz ? '#f8fafc' : '#000', borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.06)' }}>
            {!showQuiz ? (
              activeVideo && <video ref={videoRef} key={activeVideo.id} controls controlsList="nodownload noremoteplayback" disablePictureInPicture onContextMenu={e => e.preventDefault()} onTimeUpdate={handleTimeUpdate} onEnded={handleEnded} style={{ width: '100%', display: 'block', maxHeight: '62vh' }} src={'/api/videos/stream/' + activeVideo.id + '?token=' + token} />
            ) : (
              <div style={{ width: '100%', minHeight: isMobile ? '50vh' : '62vh', padding: isMobile ? '24px 16px' : '40px', boxSizing: 'border-box', overflowY: 'auto' }}>
                {quizState === 'intro' && quiz && (
                  <div style={{ textAlign: 'center', color: '#0f172a', maxWidth: 600, margin: '0 auto' }}>
                    <div style={{ fontSize: 50, marginBottom: 10 }}>📝</div>
                    <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 12 }}>{quiz.title}</h2>
                    <p style={{ color: '#64748b', fontSize: 15, marginBottom: 24, lineHeight: 1.5 }}>{quiz.description || 'Eğitimi başarıyla tamamlamak için bu sınavı geçmelisiniz.'}</p>
                    <div style={{ display: 'inline-flex', gap: 16, background: '#f1f5f9', padding: '12px 24px', borderRadius: 12, marginBottom: 32 }}>
                       <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>SORU SAYISI</div><div style={{ fontSize: 18, fontWeight: 800 }}>{quiz.questions?.length}</div></div>
                       <div style={{ width: 1, background: '#cbd5e1' }} />
                       <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>GEÇER NOT</div><div style={{ fontSize: 18, fontWeight: 800 }}>%{quiz.passing_score}</div></div>
                    </div>
                    {quizResult && (
                      <div style={{ marginBottom: 24, padding: 12, borderRadius: 12, background: quizResult.passed ? '#dcfce7' : '#fee2e2', color: quizResult.passed ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                        Son Denemeniz: %{quizResult.score} ({quizResult.passed ? 'Geçtiniz' : 'Kaldınız'})
                      </div>
                    )}
                    <button onClick={() => { setQuizState('taking'); setAnswers({}); }} style={{ display: 'block', width: '100%', background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', padding: '16px', borderRadius: 14, border: 'none', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,122,255,0.3)' }}>
                      {quizResult ? 'Sınavı Tekrar Çöz' : 'Sınava Başla'}
                    </button>
                  </div>
                )}

                {quizState === 'taking' && quiz && (
                  <div style={{ color: '#0f172a', maxWidth: 700, margin: '0 auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                       <div style={{ fontWeight: 800, fontSize: 18 }}>{quiz.title}</div>
                       <div style={{ fontWeight: 600, color: '#007aff', fontSize: 14 }}>{Object.keys(answers).length} / {quiz.questions.length} Çözüldü</div>
                    </div>
                    {quiz.questions.map((q, i) => (
                      <div key={i} style={{ marginBottom: 32, background: 'white', padding: isMobile ? 16 : 24, borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, lineHeight: 1.5 }}>
                           <span style={{ color: '#007aff', marginRight: 8 }}>{i+1}.</span>{q.question}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {q.options.map((opt, oIndex) => (
                            <div key={oIndex} onClick={() => setAnswers({...answers, [i]: oIndex})} style={{ padding: '14px 16px', background: answers[i] === oIndex ? '#eff6ff' : '#f8fafc', border: `1.5px solid ${answers[i] === oIndex ? '#3b82f6' : 'transparent'}`, borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.2s' }}>
                              <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${answers[i] === oIndex ? '#3b82f6' : '#cbd5e1'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                 {answers[i] === oIndex && <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6' }} />}
                              </div>
                              <span style={{ fontSize: 15, fontWeight: answers[i] === oIndex ? 600 : 400, color: answers[i] === oIndex ? '#1e3a8a' : '#334155' }}>{opt}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <button onClick={handleQuizSubmit} disabled={Object.keys(answers).length !== quiz.questions.length} style={{ width: '100%', background: 'linear-gradient(135deg, #34c759, #30b350)', color: 'white', padding: '16px', borderRadius: 14, border: 'none', fontSize: 16, fontWeight: 700, cursor: Object.keys(answers).length !== quiz.questions.length ? 'not-allowed' : 'pointer', opacity: Object.keys(answers).length !== quiz.questions.length ? 0.5 : 1, boxShadow: '0 8px 20px rgba(52,199,89,0.3)' }}>
                      Sınavı Bitir
                    </button>
                  </div>
                )}

                {quizState === 'result' && quizResult && (
                  <div style={{ textAlign: 'center', color: '#0f172a', maxWidth: 600, margin: '40px auto 0' }}>
                    <div style={{ fontSize: 72, marginBottom: 16 }}>{quizResult.passed ? '🎉' : '❌'}</div>
                    <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 12 }}>{quizResult.passed ? 'Tebrikler, Geçtiniz!' : 'Maalesef Kaldınız'}</h2>
                    <div style={{ fontSize: 56, fontWeight: 800, color: quizResult.passed ? '#16a34a' : '#dc2626', marginBottom: 24 }}>%{quizResult.score}</div>
                    <p style={{ color: '#64748b', fontSize: 16, marginBottom: 32 }}>Doğru Sayısı: <strong>{quizResult.correct}</strong> / {quizResult.total}</p>
                    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button onClick={() => { setQuizState('intro'); }} style={{ background: '#e2e8f0', color: '#334155', padding: '14px 24px', borderRadius: 30, border: 'none', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Başa Dön</button>
                      {quizResult.passed && (
                        <button onClick={handleViewCertificate} style={{ background: 'linear-gradient(135deg, #34c759, #30b350)', color: 'white', padding: '14px 24px', borderRadius: 30, border: 'none', fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 20px rgba(52,199,89,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}><Award size={18} /> Sertifikayı Gör</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Genel Bilgiler & Notlarım Sekmesi */}
          {!showQuiz && (
            <>
              <div style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 14, padding: '18px 22px', marginTop: 14, border: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontWeight: 700, fontSize: 19, color: 'white', marginBottom: 4 }}>{course?.title}</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 8 }}>{activeVideo?.title}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'rgba(255,255,255,0.4)', fontSize: 12 }}><Clock size={12} /> Toplam Süre: {totalDuration}dk</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 14, overflow: 'hidden', marginTop: 12, border: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0 20px' }}>
                  {[['info', BookOpen, 'Genel Bilgiler'], ['notes', FileText, 'Notlarım']].map(([key, Icon, label]) => (
                    <button key={key} onClick={() => setActiveTab(key)} style={{ padding: '13px 16px 13px 0', marginRight: 20, border: 'none', background: 'none', cursor: 'pointer', fontWeight: activeTab===key?700:400, color: activeTab===key?'#E30613':'rgba(255,255,255,0.4)', fontSize: 13, borderBottom: activeTab===key?'2px solid #E30613':'2px solid transparent', marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon size={13} /> {label}
                    </button>
                  ))}
                </div>
                <div style={{ padding: '18px 22px' }}>
                  {activeTab === 'info' && <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', lineHeight: 1.8 }}>{course?.description || 'Bu eğitim hakkında açıklama bulunmamaktadır.'}</div>}
                  {activeTab === 'notes' && (
                    <div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                        <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Yeni not ekle..." style={{ flex: 1, minHeight: 72, padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 13, outline: 'none', resize: 'none', fontFamily: 'Inter, sans-serif', boxSizing: 'border-box', color: 'white' }} />
                        <button onClick={() => { if (!note.trim()) return; api.post('/notes', { video_id: activeVideo?.id, content: note }).then(r => { setSavedNotes(prev => [r.data, ...prev]); setNote(''); setNoteSaved(true); setTimeout(() => setNoteSaved(false), 1500); }); }} style={{ background: noteSaved ? '#34c759' : 'linear-gradient(135deg, #E30613, #8b0000)', color: 'white', border: 'none', borderRadius: 10, padding: '0 14px', cursor: 'pointer', fontWeight: 700, fontSize: 12, flexShrink: 0, minWidth: 60 }}>
                          {noteSaved ? '✓' : '+ Ekle'}
                        </button>
                      </div>
                      {savedNotes.length === 0 && <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 12, padding: '16px 0' }}>Henüz not eklenmedi</div>}
                      {savedNotes.length > 0 && (
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                          {savedNotes.map((n, i) => (
                            <div key={n.id || i} style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 12px', marginBottom: 8, borderLeft: '3px solid #E30613', display: 'flex', gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>{n.content}</div>
                                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>{new Date(n.created_at).toLocaleString('tr-TR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}</div>
                              </div>
                              <button onClick={() => { api.delete('/notes/' + n.id).then(() => setSavedNotes(prev => prev.filter(x => x.id !== n.id))); }} style={{ background: 'none', border: 'none', color: 'rgba(255,59,48,0.6)', cursor: 'pointer', fontSize: 14, padding: '0 4px', flexShrink: 0 }}>✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ width: isMobile?'100%':300, flexShrink: 0, background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', position: isMobile?'relative':'sticky', top: isMobile?0:76 }}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: percent===100?'#34c759':'#f57c00', fontWeight: 700, fontSize: 14 }}>%{percent} tamamlanan {percent===99 && quiz && (!quizResult || !quizResult.passed) ? <span style={{fontSize:11, color:'#f59e0b'}}>(Sınav Bekliyor)</span> : ''}</span>
              <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>{completedVideos}/{totalVideos}</span>
            </div>
            <div style={{ height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: percent+'%', background: percent===100?'linear-gradient(90deg, #34c759, #00b4d8)':'linear-gradient(90deg, #f57c00, #ff9500)', borderRadius: 4, transition: 'width 0.5s' }} />
            </div>
          </div>
          <div style={{ maxHeight: isMobile?'300px':'calc(100vh - 220px)', overflowY: 'auto' }}>
            {course?.sections?.map(section => (
              <div key={section.id}>
                <div style={{ padding: '10px 18px', background: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid rgba(255,255,255,0.04)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  <span style={{ color: '#E30613' }}>▼</span> {section.title}
                </div>
                {section.videos?.map(video => {
                  const vIndex = allVideos.findIndex(v => v.id === video.id);
                  const isActive = !showQuiz && activeVideo?.id === video.id;
                  const isCompleted = progressMap[video.id];
                  const isLocked = vIndex > 0 && !progressMap[allVideos[vIndex-1]?.id] && !isCompleted;
                  return (
                    <div key={video.id} onClick={() => !isLocked && goToVideo(video, vIndex)}
                      style={{ padding: '11px 18px 11px 24px', cursor: isLocked?'not-allowed':'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)', background: isActive?'rgba(227,6,19,0.15)':'transparent', display: 'flex', alignItems: 'center', gap: 10, opacity: isLocked?0.4:1 }}
                      onMouseEnter={e => { if (!isActive && !isLocked) e.currentTarget.style.background='rgba(255,255,255,0.04)'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background=isActive?'rgba(227,6,19,0.15)':'transparent'; }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid '+(isCompleted?'#34c759':isActive?'#E30613':'rgba(255,255,255,0.2)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: isCompleted?'rgba(52,199,89,0.15)':isActive?'rgba(227,6,19,0.15)':'transparent' }}>
                        {isCompleted?<span style={{ color: '#34c759', fontSize: 10, fontWeight: 700 }}>✓</span>:isLocked?<span style={{ fontSize: 9 }}>🔒</span>:isActive?<span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E30613', display: 'block' }} />:null}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: isActive?'#60a5fa':'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: isActive?600:400 }}>{video.title}</div>
                        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, marginTop: 2 }}>▶ {video.duration||0}dk</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            
            {/* Sınav Bölümü Menü Öğesi */}
            {quiz && (
              <div>
                <div style={{ padding: '10px 18px', background: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid rgba(255,255,255,0.04)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  <span style={{ color: '#f59e0b' }}>▼</span> DEĞERLENDİRME
                </div>
                <div onClick={() => allVideosDone && goToVideo({ isQuiz: true }, allVideos.length)}
                  style={{ padding: '11px 18px 11px 24px', cursor: allVideosDone?'pointer':'not-allowed', borderBottom: '1px solid rgba(255,255,255,0.04)', background: showQuiz?'rgba(245,158,11,0.15)':'transparent', display: 'flex', alignItems: 'center', gap: 10, opacity: allVideosDone?1:0.4 }}
                  onMouseEnter={e => { if (!showQuiz && allVideosDone) e.currentTarget.style.background='rgba(255,255,255,0.04)'; }}
                  onMouseLeave={e => { if (!showQuiz) e.currentTarget.style.background='transparent'; }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid '+(quizResult?.passed?'#34c759':showQuiz?'#f59e0b':'rgba(255,255,255,0.2)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: quizResult?.passed?'rgba(52,199,89,0.15)':showQuiz?'rgba(245,158,11,0.15)':'transparent' }}>
                    {quizResult?.passed?<span style={{ color: '#34c759', fontSize: 10, fontWeight: 700 }}>✓</span>:!allVideosDone?<span style={{ fontSize: 9 }}>🔒</span>:showQuiz?<span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', display: 'block' }} />:<ClipboardList size={10} color="rgba(255,255,255,0.5)" />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: showQuiz?'#fcd34d':'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: showQuiz?600:400 }}>{quiz.title}</div>
                    <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, marginTop: 2 }}>{quizResult?.passed ? `Geçti (%${quizResult.score})` : 'Eğitim Sonu Sınavı'}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showRatingModal && <RatingModal courseTitle={course?.title} courseId={id} onClose={() => setShowRatingModal(false)} />}
    </div>
  );
}
